import type {Component, Property, PropertyProps} from 'grapesjs';
import type {SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerSavePayloadSources} from '@EmailMakerReact/lib/email-maker-save-payload-library';

/** Value of a GrapesJS component-view `events` entry: a view method name or a DOM event handler. */
export type ComponentViewEventHandler = string | ((e: Event) => void) | ((e: MouseEvent) => void) | ((e: TouchEvent) => void);

export type ComponentViewEvents = Record<string, ComponentViewEventHandler>;

export interface ImportComponentsOptions {
  isEditorAiContentGenerationEnabled: boolean;
  /** Getter keeps the read lazy: the flag is toggled at runtime by the TOGGLE_AI_EDIT command. */
  isAiEditEnabled: () => boolean;
}

export interface EmailMakerPluginDeps {
  store: EmailMakerImmutableStore;
  savePayloadSources: EmailMakerSavePayloadSources;
  componentsOptions: ImportComponentsOptions;
}

export enum EmailMakerBannerStyle {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  AI = 'ai',
}

export interface EmailMakerBannerOptions {
  message: string;
  style: EmailMakerBannerStyle;
  icon?: string;
  cta?: VoidFunction;
  ctaText?: string;
}

export interface OpenDesignSelectorOptions {
  /** Runs after the designSelector script loads, just before the selector opens. */
  beforeLoad?: VoidFunction;
  isMultiselectAllowed?: boolean;
  addCheckmarkOnSelection?: boolean;
  showPageLimitWarning?: boolean;
}

export enum SaveActionType {
  ACCEPT_YOURS = 'ACCEPT_YOURS',
  ACCEPT_THEIRS = 'ACCEPT_THEIRS',
}

export type StyleAttributes = Record<string, string>;

export interface BasicComponentCommandProps {
  component?: Component;
}

export interface SymbolStorageData {
  content?: string;
  type?: string;
}

export enum ResponsiveType {
  MOBILE = 'MOBILE',
  TABLET = 'TABLET',
}

export enum ResponsiveAttributeName {
  MOBILE = 'responsive',
  TABLET = 'responsive-tablet',
}

export enum OnLoadUrlParamsConfig {
  AutoUpdateEmailContentParams = 'autoUpdateEmailContentParams',
}

export type ComponentAlignmentStyle = 'middle' | 'right' | 'center' | 'left';

export interface EmailPreviewResponse {
  fileName: string;
  html: string;
}

export interface UserSignupFormsData {
  limit: number;
  offset: number;
  signupForms: UserSignupFormLinkData[];
}

export const MAILING_LIST_UPDATE_EVENT = 'mailingList::update';

export interface UserSignupFormLinkData {
  name: string;
  url: string;
}

export interface AutoUpdateTextContentParams {
  rewriteContentPrompt: string;
  rewriteContentPromptOptions: SelectedValueType;
}

export type HexColors = string[];

export interface AutoUpdateBrandingParams {
  colors: HexColors;
}

export interface GA4TrackingEvents {
  SECTIONS_USED: number;
}

export const PROMPT_SESSION_STORAGE_VAR = 'prompt-session-storage-var';
export const GENERATION_OPTIONS_SESSION_STORAGE_VAR = 'generation-options-session-storage-var';
export const TEMPLATE_AI_REGENERATION = 'template-ai-regeneration';
export const TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR = 'toolbar-ai-rewrite-dropdown-anchor';
export const DISABLE_GJS_TOOLBAR_ACTIVE_STYLE = 'disable-gjs-toolbar-active-style';
export const AI_REWRITE_PROMPT_BOX_TOOLBAR_INDEX = 3;

export const EMAIL_MAKER_AI_REWRITE_PROMPT = 'email-maker-ai-rewrite-prompt';
export const EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS = 'email-maker-ai-rewrite-generation-options';
export const EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT = 'email-maker-ai-rewrite-previous-prompt';
export const EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST = 'email-maker-ai-redesign-loading-toast';
export const EMAIL_MAKER_AI_REWRITE_LOADING_TOAST = 'email-maker-ai-rewrite-loading-toast';
export const EMAIL_MAKER_AI_REDESIGN_SIGNUP_SOURCE = 'email-maker-ai-redesign';
export const EMAIL_MAKER_AI_REWRITE_SIGNUP_SOURCE = 'email-maker-ai-rewrite';
export const EMAIL_MAKER_AI_REWRITE_DROPDOWN_SIGNUP_SOURCE = 'email-maker-ai-rewrite-dropdown';

export enum ComponentLoadingOverlayStylesTarget {
  COMPONENT_DEFINITION = 'COMPONENT_DEFINITION',
  ELEMENT = 'ELEMENT',
}

export enum EmailOverlayStyle {
  LOADING,
  AI_EDIT,
}

export enum EmailMediaOverlayVariant {
  HOVER = 'hover',
  DROP = 'drop',
}

export interface ShowMediaComponentOverlayOptions {
  element?: HTMLElement;
  variant?: EmailMediaOverlayVariant;
}

export interface HideMediaComponentOverlayOptions {
  element?: HTMLElement;
}

export interface ComponentLoadingOverlayStyle {
  width: string;
  marginLeft: string;
}

export interface CanUseBypassedEmailResponse {
  canUseEmail: boolean;
}

export interface ExtendedControlIcons {
  first: string;
  second: string;
  third: string;
  fourth: string;
}

export interface SliderStyleProps extends PropertyProps {
  component?: Component;
  step?: number;
  min?: number;
  max?: number;
  units?: string;
  inputType?: string;
  extendedControls?: boolean;
  property: string;
  propertyLabel?: string;
  defaults?: string;
  isResponsive?: boolean;
  toggleProp?: string;
  extendedControlIcons?: ExtendedControlIcons;
}

export interface SliderChangeParams {
  el: HTMLElement;
  event: Event;
  complete?: unknown;
  propertyName?: string;
  isSingleProperty?: boolean;
}

export interface SliderStyleObject {
  topStyle: number;
  bottomStyle: number;
  rightStyle: number;
  leftStyle: number;
}

export interface SliderCallbackData {
  el: HTMLElement;
  createdEl?: HTMLElement;
  property: Property;
  props: SliderStyleProps;
  change: (...args: unknown[]) => void;
  updateStyle: (value: string, opts?: unknown) => void;
}

export interface SliderUpdateData extends SliderCallbackData {
  value: string | number;
}

export interface SocialLinkData {
  id: string;
  name: string;
  baseURL: string;
  logos: Record<string, string>;
}

export interface SocialLinksCreateData {
  props: SocialLinksStyleProps;
  change: (...args: unknown[]) => void;
}

export interface SocialLinksStyleProps extends PropertyProps {
  component?: Component;
}

export interface SocialLinkInputCustomHandlers {
  onInputFocus: (dataId: string, value: string) => void;
  onInputBlur: (dataId: string, value: string) => void;
}

export interface SocialLinkInputFieldProps {
  inputClassName: string;
  placeholder: string;
  input: string;
  size: InputFieldSize;
}

export interface SocialLinkSuggestionsDropdownProps {
  popupClassName: string;
  popupAlignment: ALIGNMENT_TYPE;
  popupPosition: DROPDOWN_POSITION;
  truncateText: boolean;
  showSuggestionAsTitleOnHover: boolean;
}

export interface SocialLinkDropdownOptions {
  platform: string;
  socialLinkInputCustomHandlers: SocialLinkInputCustomHandlers;
  inputFieldProps: SocialLinkInputFieldProps;
  inputSuggestionsDropdownProps: SocialLinkSuggestionsDropdownProps;
  inputId: string;
  dropdownId: string;
}

export enum UpsertComponentMethod {
  APPEND_AFTER,
  INSERT_INTO,
  REPLACE_WITH,
  PREPEND_BEFORE,
}

export interface AsyncGetPreviewSuccessResponse {
  creatorID: string;
  isPremium: boolean;
  isInternal: boolean;
  isReady: string;
  url: string;
}

export enum AsyncGetPreviewErrorCodes {
  POSTER_NOT_PUBLIC = 'POSTER_NOT_PUBLIC',
  POSTER_NOT_FOUND = 'POSTER_NOT_FOUND',
  ERROR = 'ERROR',
}
